import React, {
  useEffect,
  useCallback,
  useMemo,
  useState,
  useRef,
  forwardRef,
} from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { AgGridReact } from "ag-grid-react"; // the AG Grid React Component
import {
  AllEnterpriseModule,
  LicenseManager,
  ModuleRegistry,
} from "ag-grid-enterprise";
import { provideGlobalGridOptions } from "ag-grid-community";
import {
  saveFilterModel,
  saveColumnModel,
  saveGroupState,
  initializeState,
  setToolPanel,
  setRowGroup,
  setSelection,
  clearSelection,
  setSavedQueriesPanel,
  saveScrollPosition,
} from "../../redux/reducers/gridLayoutReducer";
import { getGridInititalState } from "../../constants/dataGridConfig";
import { SFStack, SFGridItem, SFGridContainerItem } from "../grid";
import FilterChips from "./filterChips";
import getStyled from "../../hooks/getStyled";
import { Logger } from "@base-provider";
import CreateReactPortal from "../../hooks/createReactPortal";
import SFBottomStatusPanel from "./renderers/statusPanel";
import "./grid.scss";
import _ from "lodash";
import CardContainer from "./gridCardView";
import SFBasicFilters from "./basicFiltering";
import AdvancedQuery from "./advancedQuery";
import NoRowsOverlay from "./noRowsOverlay";
import SFSavedQueryDrawer from "../savedQueryDrawer";
import { SFBarChartNoBorderIcon, SFCloseIcon } from "../icons";
import FilterAltIcon from "@mui/icons-material/FilterAlt";
import SFIconButton from "../button/iconButton";

if (process.env?.REACT_APP_AG_GRID_LICENSE_KEY) {
  ModuleRegistry.registerModules([AllEnterpriseModule]);
  provideGlobalGridOptions({ theme: "legacy" });
  LicenseManager.setLicenseKey(process.env.REACT_APP_AG_GRID_LICENSE_KEY);
}

const { styled } = getStyled();

const SFStackStyled = styled(SFStack)(({ theme, borderBottom }) => ({
  padding: theme.spacing(2),
  backgroundColor: theme.palette.white.main,
  borderTopLeftRadius: "4px",
  borderTopRightRadius: "4px",
  borderBottom: borderBottom ? "1px solid rgba(0, 0, 0, 0.12)" : "",
}));

const StyledGridWrapper = styled("div", { shouldForwardProp: false })(
  ({ theme, hasBorder, isopen }) => ({
    height: "100%",
    width: "100%",
    border: hasBorder ? "1px solid rgba(0, 0, 0, 0.12)" : "none",
    borderRadius: hasBorder ? 4 : 0,
    display: "flex",
    flexDirection: "column",
    "& .ag-theme-ey": {
      zIndex: 1000,
      display: "block",
    },
  }),
);

const SFDataGridCore = forwardRef(
  (
    {
      cardVariant = "card",
      columnDefs = [],
      // Custom props that should NOT be passed to AgGridReact
      gridName,
      detailColumnDefs,
      datasourceUrl,
      filterValuesUrl,
      viewDetailsUrl,
      viewMasterDetailsUrl,
      rowCountUrl,
      customRenderers,
      customComponents,
      rowInactiveOuterClassRule,
      fitScreen,
      hasRowSelection,
      basicFiltering,
      disableSaveSelection,
      loadingRows,
      enableStatusBar,
      persistState,
      getValues,
      allowMultipleDetailRows,
      serverSideInfiniteScroll,
      serverSideDataSource,
      postFilterChange,
      beforeDownloadFilter,
      enableResponseCaching,
      hasColSelection,
      hideGrid,
      cacheRefreshInterval,
      cacheMinAge,
      permissions,
      entityType,
      getCopiedRowData,
      onSortChangedFilterModel,
      suppressRowClickSelection,
      rowMultiSelectWithClick,
      headerCheckboxSelection,
      allowSingleRowSelection,
      isRowSelectable,
      card,
      cardColumns,
      subBar,
      oldFiltersPanel,
      suppressFilterChips,
      gridConfig,
      disableFilterSideBar,
      onToggle,
      noRowsMessage,
      hasBorder,
      minHeight,
      disableSelectedRowStatus,
      advancedFilterProps,
      onGroupChanged,
      postSortChange,
      postColumnPositionChange,
      rowDragging,
      disableRowDragging,
      disableDefaultRowDragger,
      onRowCountChange,
      configureRequestPayload,
      canDownload,
      canEdit,
      search,
      searchLabel,
      exportFileName,
      groupByOptions,
      // Tree data props (server-side hierarchical data)
      treeData,
      treeDataChildrenUrl, // Custom prop - NOT an AG Grid prop
      treeDataSelectionConfig, // Custom prop - NOT an AG Grid prop
      isServerSideGroup,
      getServerSideGroupKey,
      // Valid AG Grid props to be spread
      ...agGridProps
    },
    ref,
  ) => {
    // Re-create props object for internal usage (backward compatibility)
    const props = {
      gridName,
      detailColumnDefs,
      datasourceUrl,
      filterValuesUrl,
      viewDetailsUrl,
      viewMasterDetailsUrl,
      rowCountUrl,
      customRenderers,
      customComponents,
      rowInactiveOuterClassRule,
      fitScreen,
      hasRowSelection,
      basicFiltering,
      disableSaveSelection,
      loadingRows,
      enableStatusBar,
      persistState,
      getValues,
      allowMultipleDetailRows,
      serverSideInfiniteScroll,
      serverSideDataSource,
      postFilterChange,
      beforeDownloadFilter,
      enableResponseCaching,
      hasColSelection,
      hideGrid,
      cacheRefreshInterval,
      cacheMinAge,
      permissions,
      entityType,
      getCopiedRowData,
      onSortChangedFilterModel,
      suppressRowClickSelection,
      rowMultiSelectWithClick,
      headerCheckboxSelection,
      allowSingleRowSelection,
      isRowSelectable,
      card,
      cardColumns,
      subBar,
      oldFiltersPanel,
      suppressFilterChips,
      gridConfig,
      disableFilterSideBar,
      onToggle,
      noRowsMessage,
      hasBorder,
      minHeight,
      disableSelectedRowStatus,
      advancedFilterProps,
      onGroupChanged,
      postSortChange,
      postColumnPositionChange,
      rowDragging,
      disableRowDragging,
      disableDefaultRowDragger,
      onRowCountChange,
      configureRequestPayload,
      canDownload,
      canEdit,
      search,
      searchLabel,
      exportFileName,
      groupByOptions,
      // Tree data props
      treeData,
      treeDataChildrenUrl,
      treeDataSelectionConfig,
      isServerSideGroup,
      getServerSideGroupKey,
      ...agGridProps,
    };
    const savedLayout = useSelector((state) => {
      return state.gridLayout.find(
        (gridLayout) => gridLayout.gridName === props.gridName,
      );
    });

    const { t } = useTranslation();
    const dispatch = useDispatch();

    //setting up the isActive column definition (if exists) and the value formatter for null/empty values for all column defs
    const colDefs = useMemo(() => {
      //Default handling for isActive columns
      //added a value formatter that is used by chips and basic filtering
      //to replace the true /false values with Active/inactive literals
      let isActiveDef = columnDefs.find((def) => def.field === "isActive");
      if (isActiveDef) {
        isActiveDef.headerName =
          isActiveDef.headerName ??
          t("components.datagrid.isActiveCol.headerName");
        let valueFormatter = (params) => {
          return params?.value?.toString() === "true"
            ? t("components.datagrid.isActiveCol.trueLiteral")
            : t("components.datagrid.isActiveCol.falseLiteral");
        };
        isActiveDef.valueFormatter = valueFormatter;
        isActiveDef.filterParams = {
          ...(isActiveDef.filterParams ?? {}),
          valueFormatter,
        };
      }
      columnDefs.forEach((c) => {
        c.valueFormatter =
          c.valueFormatter ??
          ((params) => {
            return params?.value === null || params?.value === ""
              ? t("components.datagrid.filters.blankValue")
              : params.value;
          });
      });
      return columnDefs;
    }, [columnDefs]);

    //this will be used to pre-apply any filter/sort model to the grid (if exists) before making the server side call
    const [filterOnFirstLoad, setFilterOnFirstLoad] = useState(true);
    // Track when we're restoring selection state from Redux (to avoid recalculating child counts)
    const isRestoringSelection = useRef(false);
    const rowGroupHandler = (column) => {
      if (column) {
        if (props.persistState) {
          dispatch(
            saveGroupState({
              gridName: props.gridName,
              rowGroupKey: column.field,
            }),
          );
        }
        ref.current.api.setRowGroupColumns([column.field]);
        ref.current.api.setColumnsVisible([column.field], false);
      } else {
        if (props.persistState) {
          dispatch(
            saveGroupState({ gridName: props.gridName, rowGroupKey: null }),
          );
        }
        ref.current.api.setRowGroupColumns([]);
      }

      if (props.onGroupChanged) {
        props.onGroupChanged(column);
      }
    };

    const [panelState, setPanelState] = useState({ open: false });

    const toolPanel = savedLayout?.toolPanel;
    const rowGroup = savedLayout?.rowGroup;

    useEffect(() => {
      if (!filterOnFirstLoad && props.serverSideDataSource) {
        let datasource = props.serverSideDataSource();
        ref.current.api.setGridOption("serverSideDatasource", datasource);
      }
    }, [filterOnFirstLoad]);

    useEffect(() => {
      if (savedLayout) {
        // When the grid's redux state is ready, close any tool panels that might have been left open
        // and save in redux the columns that have row grouping enabled
        dispatch(
          setToolPanel({
            gridName: props.gridName,
            open: false,
            name: null,
          }),
        );

        dispatch(
          setSavedQueriesPanel({
            gridName: props.gridName,
            open: false,
          }),
        );

        dispatch(
          setRowGroup({
            gridName: props.gridName,
            options: colDefs
              .filter((x) => x.enableRowGroup)
              .map((x) => ({
                label: x.headerName,
                field: x.field,
              })),
          }),
        );
      }
    }, [!!savedLayout]);

    useEffect(() => {
      // When the toolpanel redux state changes, update the visibility of the grid's tool panel
      if (!toolPanel || !ref.current?.api) return;

      if (toolPanel.open) {
        ref.current.api.openToolPanel(toolPanel.name);
      } else {
        ref.current.api.closeToolPanel();
      }
      // We add the grid's ref as a dependency to handle redux state loading before the grid is ready
    }, [toolPanel?.open, toolPanel?.name, !!ref.current?.api]);

    useEffect(() => {
      // When the current row grouping redux state changes we apply it to the grid
      if (!ref.current?.api) return;
      rowGroupHandler(rowGroup.current); // We add the grid's ref as a dependency to handle redux state loading before the grid is ready
    }, [rowGroup?.current?.field]);

    const statusBar = useMemo((params) => {
      return {
        statusPanels: [
          {
            statusPanel: () => (
              <SFBottomStatusPanel
                gridName={props.gridName}
                hasRowSelection={props.hasRowSelection}
                disableSelectedRowStatus={props.disableSelectedRowStatus}
                treeSelectionLabels={props.treeDataSelectionConfig ? {
                  parentLabel: props.treeDataSelectionConfig.parentLabel,
                  childLabel: props.treeDataSelectionConfig.childLabel,
                } : null}
              />
            ),
            align: "left",
          },
        ],
      };
    }, []);

    //setting up default column definitions
    const defaultColDef = useMemo(() => {
      return {
        resizable: true,
        sortable: true,
        filter: !props.basicFiltering,
        floatingFilter: false,
        filterParams: {
          suppressSelectAll: true,
          defaultToNothingSelected: true,
          refreshValuesOnOpen: true,
          values: props.getValues,
        },
        //hides menu, filter and context buttons shown next to column headers
        suppressHeaderMenuButton: true,
        suppressHeaderFilterButton: true,
        suppressHeaderContextMenu: true,
      };
    }, []);

    const sideBar = useMemo(() => {
      return {
        toolPanels: [
          ...(props.hasColSelection
            ? [
              {
                id: "columns",
                labelDefault: "columns",
                labelKey: "columns",
                iconKey: "columns",
                toolPanel: "agColumnsToolPanel",
                toolPanelParams: {
                  suppressRowGroups: true,
                  suppressValues: true,
                  suppressPivots: true,
                  suppressPivotMode: true,
                  suppressColumnSelectAll: true,
                  suppressColumnExpandAll: true,
                },
              },
            ]
            : []),
          ...(props.basicFiltering
            ? [
              {
                id: "basicFilters",
                labelDefault: "basicFilters",
                labelKey: "basicFilters",
                iconKey: "filter",
                toolPanel: SFBasicFilters,
              },
            ]
            : [
              {
                id: "filters",
                labelDefault: "Filters",
                labelKey: "filters",
                iconKey: "filter",
                toolPanel: "agFiltersToolPanel",
              },
            ]),
        ],
        position: "right",
        hiddenByDefault: props.basicFiltering && !props.hasColSelection,
        defaultToolPanel: "",
      };
    }, []);

    useEffect(() => {
      if (panelState.open) {
        const panel = document.querySelector(".ag-side-bar");
        const grid = document.querySelector(".ag-root");
        grid.tabIndex = 0;
        panel.tabIndex = 0;
        panel.focus();

        const handleEscape = (event) => {
          if (event.key === "Escape") {
            ref.current.api.closeToolPanel();
            grid.focus();
          }
        };

        panel.addEventListener("keydown", handleEscape);

        return () => {
          panel.removeEventListener("keydown", handleEscape);
        };
      }
    }, [panelState.open]);

    useEffect(() => {
      const toggleOpen = (_props) => {
        const _open = ref.current?.api?.isToolPanelShowing();
        setPanelState((curState) => ({ ...curState, open: _open }));
      };
      ref.current?.api?.addEventListener("toolPanelVisibleChanged", toggleOpen);

      return () => {
        //check if api is destroyed before removing event listeners
        if (ref.current?.api?.isDestroyed() === false) {
          ref.current?.api?.removeEventListener(
            "toolPanelVisibleChanged",
            toggleOpen,
          );
        }
      };
    }, [!!ref.current?.api]);

    useEffect(() => {
      if (
        ((!savedLayout && props.gridConfig) || !savedLayout) &&
        props.persistState
      ) {
        let initialGridConfig = getGridInititalState(
          props.gridName,
          props.gridConfig ?? null,
        );

        if (props.rowModelType === "clientSide") {
          initialGridConfig.gridConfig.rowData = props.rowData;
        }

        const isActiveDef = colDefs.find((def) => def.field === "isActive");

        if (isActiveDef && isActiveDef.filter !== false) {
          initialGridConfig.gridConfig.filterModel[isActiveDef.field] = {
            values: ["true"],
            filterType: "set",
            displayName: isActiveDef.headerName,
          };
        }

        dispatch(initializeState(initialGridConfig));
      }
    }, []);

    const gridSizeChangeHandler = useCallback((params) => {
      if (!props.serverSideDataSource || props.fitScreen) {
        params.api.sizeColumnsToFit();
      }
    }, []);

    const handleGridReady = useCallback(
      (params) => {
        // Mark that we're restoring state from Redux to prevent clearing selection during filter restoration
        isRestoringSelection.current = true;

        if (savedLayout && Object.keys(savedLayout.filterModel).length !== 0) {
          params.api.setFilterModel(savedLayout.filterModel);
        } else if (!props.persistState && props.gridConfig?.filterModel) {
          params.api.setFilterModel(props.gridConfig.filterModel);
        }

        //Column state (sort, columns order and columns selection)
        if (savedLayout?.columnModel && savedLayout.columnModel.length !== 0) {
          params.api.applyColumnState({
            state: savedLayout.columnModel,
            applyOrder: true,
          });
        } else if (!props.persistState && props.gridConfig?.sortModel) {
          params.api.applyColumnState({
            state: props.gridConfig.sortModel,
          });
        }

        //setting column group and hiding column
        if (savedLayout && savedLayout.rowGroupKey) {
          params.api.setRowGroupColumns([savedLayout.rowGroupKey]);
          params.api.setColumnVisible(savedLayout.rowGroupKey, false);
        }

        //apply any existing selection to the datagrid from redux
        if (
          savedLayout &&
          (savedLayout.selection.allSelected ||
            savedLayout.selection.toggledNodes.length !== 0 ||
            savedLayout.selection.rawSelectionState)
        ) {
          if (!props.disableSaveSelection) {
            // Use raw AG Grid selection state if available (works for both tree data and non-tree data)
            if (savedLayout.selection.rawSelectionState) {
              params.api.setServerSideSelectionState(savedLayout.selection.rawSelectionState);
            } else {
              // Fallback for backwards compatibility
              params.api.setServerSideSelectionState({
                selectAll: savedLayout.selection.allSelected,
                toggledNodes: savedLayout.selection.toggledNodes,
              });
            }
          }
          //reset selection on load if disable flag is passed
          else {
            dispatch(clearSelection({ gridName: props.gridName }));
          }
        }

        if (
          //if server side data source is enabled AND
          //no redux state exists and initial grid config filter model is empty OR no filter exists in redux
          props.serverSideDataSource &&
          ((!savedLayout &&
            Object.keys(props.gridConfig?.filterModel ?? {}).length === 0) ||
            (savedLayout && Object.keys(savedLayout?.filterModel).length === 0))
        ) {
          //if no filter exists in redux then set flag to false
          //so the datasource can be registered on server side grids
          setFilterOnFirstLoad(false);
        }

        // Note: isRestoringSelection flag is reset in onFirstDataRendered after data loads

        Logger.of("App.Core.DataGrid.handleGridReady").info({
          params,
          hideGrid: props.hideGrid,
          props,
        });
      },
      [!!savedLayout],
    );

    const onColumnChanged = useCallback((params) => {
      let columnModel = params.api.getColumnState().map((col) => {
        return {
          colId: col.colId,
          sort: col.sort,
          hide: col.hide,
          sortIndex: col.sortIndex,
        };
      });

      if (props.persistState) {
        dispatch(
          saveColumnModel({
            gridName: props.gridName,
            columnModel: columnModel,
          }),
        );
      }

      if (props.postSortChange) {
        props.postSortChange(columnModel);
      }

      if (props.postColumnPositionChange) {
        props.postColumnPositionChange(columnModel);
      }

      // If there is a filter model, we need to update the filter model with the new column order
      if (params.context.onSortChangedFilterModel) {
        ref.current.api.setFilterModel(params.context.onSortChangedFilterModel);
      }
    }, []);

    //on first data render, if grid persists state, scroll to the last saved position if exists
    const onFirstDataRendered = useCallback((params) => {
      // Reset the restoring flag now that data has loaded
      // This allows subsequent filter changes to properly clear selection
      isRestoringSelection.current = false;

      if (
        !props.hideGrid &&
        savedLayout?.initialScrollPosition &&
        savedLayout?.initialScrollPosition !== 0
      ) {
        params.api.setRowCount(savedLayout?.initialScrollPosition + 1, false);
        params.api.ensureIndexVisible(
          savedLayout?.initialScrollPosition,
          "middle",
        );
      }
    }, []);

    const onFilterChanged = useCallback(
      (params) => {
        //this is a flag that enables server side data source
        //in the case there is a filter applied
        if (filterOnFirstLoad) {
          setFilterOnFirstLoad(false);
        }
        const filterModel = ref.current.api.getFilterModel();
        Object.keys(filterModel).forEach((key) => {
          let colDef = colDefs.find((el) => el.field === key);
          filterModel[key] = {
            ...filterModel[key],
            displayName: colDef?.headerName,
            ...(colDef?.filterParams?.numberParser &&
            colDef?.valueFormatter &&
            filterModel[key].filter
              ? { filter: colDef.valueFormatter(filterModel[key].filter) }
              : {}),
            ...(colDef?.filterParams?.numberParser &&
            colDef?.valueFormatter &&
            filterModel[key].filterTo
              ? { filterTo: colDef.valueFormatter(filterModel[key].filterTo) }
              : {}),
          };
        });

        if (props.persistState) {
          dispatch(
            saveFilterModel({
              gridName: props.gridName,
              filterModel: filterModel,
            }),
          );
        }

        //refresh selection when filter model changes
        if (props.hasRowSelection) {
          // For tree data, clear selection on filter change to prevent stale counts
          // Skip this during initial page load when restoring state from Redux
          if (props.treeData && !isRestoringSelection.current) {
            params.api.deselectAll();
            dispatch(clearSelection({ gridName: props.gridName }));
          } else if (!props.treeData) {
            onSelectionChanged(params, false);
          }
        }

        if (props.postFilterChange) {
          props.postFilterChange(filterModel, params);
        }
      },
      [filterOnFirstLoad, colDefs],
    );

    //ag grid event that triggers after any checkbox is clicked
    const onSelectionChanged = useCallback(
      (params, selectionChanged = true) => {
        // If we're restoring selection state from Redux, don't recalculate child counts
        const isUserAction = selectionChanged && !isRestoringSelection.current;
        let selection = props.onSelectionChanged(params, isUserAction);
        if (props.persistState) {
          /* Selection is a flat object by default -> IServerSideSelectionState 
          If treeData is enabled, with groupSelects 'descendants' 
          the selection is a hierarchical object -> IServerSideGroupSelectionState
            
            interface IServerSideSelectionState {
              // Whether the majority of rows are selected or not 
              selectAll: boolean;
              // All rows that have the opposite selection state to `selectAll` 
              toggledNodes: string[];
            }

            interface IServerSideGroupSelectionState {
              toggledNodes?: IServerSideGroupSelectionState[];
              nodeId?: string;
              selectAllChildren?: boolean;
            }
          */
          dispatch(
            setSelection({
              selection,
              gridName: props.gridName,
              treeData: props.treeData,
            }),
          );
        }
      },
      [],
    );

    /*
    Only visible keys have been configured.
    For the full list of keys see documentation on https://www.ag-grid.com/react-data-grid/localisation, https://www.ag-grid.com/examples/localisation/localisation/locale.en.js
    */
    const localeTextFunc = useCallback((params) => {
      return t("components.datagrid.agGrid." + params.key);
    }, []);

    //on scroll end, save the scroll position
    const onScrollEnded = useCallback((params) => {
      if (params.direction === "vertical" && props.persistState) {
        dispatch(
          saveScrollPosition({
            gridName: props.gridName,
            scrollPosition:
              params.top > 0
                ? parseInt(
                  (params.api.getLastDisplayedRowIndex() +
                      params.api.getFirstDisplayedRowIndex()) /
                      2,
                )
                : 0,
          }),
        );
      }
    }, []);

    const triggerCardLoad = (rowDataLength) => {
      ref.current.api.ensureIndexVisible(rowDataLength, "top");
    };

    const applyAdvancedQueryHandler = (query, cancel = false) => {
      let filterModel = _.cloneDeep(ref.current.api.getFilterModel());
      //get external filters from columnDefs
      let externalFiltersKeys = colDefs
        .filter((x) => x?.filterParams?.external)
        .map((x) => x.field);
      // Return filterModel with external keys and searchText
      let updatedFilterModel = cancel
        ? filterModel
        : Object.keys(filterModel)
          .filter(
            (x) => externalFiltersKeys.includes(x) || x === "searchText",
          )
          .reduce((obj, key) => {
            return {
              ...obj,
              [key]: filterModel[key],
            };
          }, {});
      if (query) {
        updatedFilterModel.advancedQuery = {
          filterType: "textSearch",
          filter: query,
          type: "contains",
        };
      } else if (updatedFilterModel.advancedQuery) {
        delete updatedFilterModel.advancedQuery;
      }
      ref.current.api.setFilterModel(updatedFilterModel);
    };

    const rowClassRules = useMemo(
      () => ({
        ...props.rowClassRules,
        "row-inactive-outer": ({ data }) => {
          if (!data) return;

          if (props.rowInactiveOuterClassRule) {
            return props.rowInactiveOuterClassRule(data);
          }
          if (Object.keys(data).includes("isActive")) return !data.isActive;
        },
      }),
      [],
    );

    const noRowsOverlayComponentParams = useMemo(
      () => ({
        noRowsMessageFunc: () =>
          props.noRowsMessage ?? t("components.datagrid.noRowsMessage"),
      }),
      [],
    );

    const loadingCellRendererParams = useMemo(
      () => ({
        loadingMessage: t("components.datagrid.loadingMessage"),
      }),
      [],
    );
    const handleToggleToolPanel = (toolPanelId) => {
      const isOpen = ref.current.api.isToolPanelShowing();
      Logger.of("App.Core.DataGrid.handleToggleFilters").info({
        isOpen,
        toolPanelId,
      });
      if (isOpen) {
        ref.current.api.closeToolPanel();
      } else {
        ref.current.api.openToolPanel(toolPanelId);
      }
    };

    const getFilterIcon = () => {
      if (props.basicFiltering || props.disableFilterSideBar) return;

      if (panelState.open && ref.current.api.getOpenedToolPanel() !== "filters")
        return;

      return (
        <SFIconButton
          type="button"
          onClick={() => handleToggleToolPanel("filters")}
          ariaLabel={t("components.datagrid.filterButtonAriaLabel")}
        >
          {panelState.open ? (
            <SFCloseIcon color={"grey500"} />
          ) : (
            <FilterAltIcon color={"grey500"} />
          )}
        </SFIconButton>
      );
    };

    const hasFilterChips =
      !props.suppressFilterChips &&
      savedLayout?.filterModel &&
      Object.keys(savedLayout?.filterModel).filter((x) =>
        colDefs.find(
          (y) =>
            y.field === x &&
            (!y.suppressFiltersToolPanel ||
              y.showChips ||
              y.context?.showChips),
        ),
      ).length !== 0;

    return (
      <>
        {CreateReactPortal(
          <AdvancedQuery
            gridName={props.gridName}
            columnDefs={colDefs}
            filterValuesUrl={props.filterValuesUrl}
            onApplyFilters={applyAdvancedQueryHandler}
            advancedFilterProps={props.advancedFilterProps}
          />,
          document.getElementById("advancedQuery"),
          false,
        )}
        {savedLayout?.savedQueriesPanel.open && (
          <SFSavedQueryDrawer
            gridName={props.gridName}
            columnDefs={colDefs}
            open={savedLayout?.savedQueriesPanel.open}
          />
        )}
        <StyledGridWrapper hasBorder={props.hasBorder}>
          {(hasFilterChips || props.oldFiltersPanel) && (
            <SFStackStyled>
              <SFGridContainerItem
                spacing={1}
                justifyContent={
                  props.basicFiltering ? "flex-start" : "flex-end"
                }
              >
                <SFGridItem xs={12} xl={11}>
                  {hasFilterChips && (
                    <FilterChips
                      savedLayout={savedLayout}
                      gridName={props.gridName}
                      onClearFilters={(filterModel) => {
                        ref.current.api.setFilterModel(filterModel);
                        //setFilterModel should refresh the grid but
                        //there's a case where i have active filter to a grid and i mark as inactive all rows
                        //the grid returns empty rows but the active filter remains
                        //when i click on clear filters btn the grid doesnt refresh
                        //this line triggers refresh of the grid
                        ref.current.api.onFilterChanged();
                      }}
                      onEditFilters={() =>
                        props.basicFiltering
                          ? dispatch(
                            setToolPanel({
                              gridName: props.gridName,
                              open: true,
                              name: "basicFilters",
                            }),
                          )
                          : ref.current.api.openToolPanel("filters")
                      }
                      columnDefs={colDefs.filter(
                        (c) =>
                          c.showChips !== false &&
                          c.context?.showChips !== false,
                      )}
                    />
                  )}
                </SFGridItem>
                {props.oldFiltersPanel && (
                  <SFGridItem xs={12} xl={1}>
                    <SFStack
                      justifyContent="flex-end"
                      direction="row"
                      spacing={1}
                    >
                      {props.onToggle && (
                        <SFIconButton
                          type="button"
                          onClick={() => {
                            props.onToggle();
                          }}
                        >
                          <SFBarChartNoBorderIcon color={"grey500"} />
                        </SFIconButton>
                      )}
                      {getFilterIcon()}
                    </SFStack>
                  </SFGridItem>
                )}
              </SFGridContainerItem>
            </SFStackStyled>
          )}
          {props.subBar && (
            <SFStackStyled borderBottom>{props.subBar}</SFStackStyled>
          )}
          <div
            style={{
              height: "100%",
              width: "100%",
              minHeight: props.minHeight ?? 300,
              overflowY: props.hideGrid ? "unset" : "initial",
            }}
          >
            <div
              style={{
                display: "flex",
                flexDirection: "row",
                ...(!props.hideGrid && { height: "100%" }),
              }}
            >
              <div style={{ flexGrow: "1" }}>
                <div
                  className={`ag-theme-ey ${
                    props.rowModelType === "clientSide" && "ag-client"
                  } ${props.hideGrid && "must-float"}
              ${props.hideGrid && panelState.open && "show-panel"}`}
                  style={{ width: "100%", height: "100%" }}
                >
                  <AgGridReact
                    {...agGridProps}
                    suppressFocusAfterRefresh
                    rowClassRules={rowClassRules}
                    // Don't set groupDisplayType for tree data - it uses auto group column
                    {...(!props.treeData && {
                      groupDisplayType: colDefs?.filter((a) => a.enableRowGroup)
                        ? "groupRows"
                        : null,
                    })}
                    ref={ref}
                    sideBar={sideBar}
                    {...(props.enableStatusBar && { statusBar })}
                    loadingCellRendererParams={loadingCellRendererParams}
                    {...(savedLayout?.initialScrollPosition && {
                      serverSideInitialRowCount: 150,
                    })}
                    defaultColDef={props.defaultColDef ?? defaultColDef}
                    masterDetail={props.masterDetail}
                    onFirstDataRendered={onFirstDataRendered}
                    context={{
                      ...props.context,
                      onSortChangedFilterModel: props.onSortChangedFilterModel, // filter model to be applied when sorting
                      masterDetail: props.masterDetail,
                      persistState: props.persistState,
                      viewDetailsUrl: props.viewDetailsUrl,
                      allowMultipleDetailRows: props.allowMultipleDetailRows,
                      filterValuesUrl: props.filterValuesUrl,
                      rowCountUrl: props.rowCountUrl,
                      savedLayout: savedLayout,
                      rowGroupHandler: rowGroupHandler,
                      isRowMaster: props.isRowMaster,
                      getCopiedRowData: props.getCopiedRowData,
                      enableResponseCaching: props.enableResponseCaching,
                      cacheRefreshInterval: props.cacheRefreshInterval,
                      cacheMinAge: props.cacheMinAge,
                      permissions: {
                        viewDetails:
                          props.permissions?.viewDetails ??
                          props.permissions?.view,
                        edit: props.permissions?.edit,
                        delete: props.permissions?.delete,
                      },
                      gridName: props.gridName,
                      //prop describing the type of entity the grid is displaying
                      entityType: props.entityType,
                    }}
                    {...(props.hasRowSelection &&
                      props.onSelectionChanged && { onSelectionChanged })}
                    popupParent={document.querySelector("body")}
                    getLocaleText={localeTextFunc}
                    components={props.customRenderers}
                    columnDefs={colDefs}
                    onGridReady={handleGridReady}
                    onSortChanged={onColumnChanged}
                    onColumnVisible={onColumnChanged}
                    onColumnMoved={onColumnChanged}
                    onFilterChanged={onFilterChanged}
                    rowData={
                      props.rowModelType === "clientSide"
                        ? props.persistState
                          ? savedLayout?.rowData
                          : props.rowData
                        : undefined
                    }
                    rowSelection={
                      props.hasRowSelection
                        ? {
                          mode: "multiRow",
                          enableClickSelection:
                              props.suppressRowClickSelection === false,
                          ...(props.rowMultiSelectWithClick && {
                            enableSelectionWithoutKeys: true,
                          }),
                          // Configure tree data selection behavior - selecting parent selects all children
                          ...(props.treeData && {
                            groupSelects: "descendants",
                          }),
                          // Enable checkboxes - disable individual checkboxes when "select all" is checked on server-side
                          checkboxes: props.headerCheckboxSelection
                            ? (params) => {
                              if (
                                params.api.getGridOption("rowModelType") ===
                                    "serverSide"
                              ) {
                                const selectionState =
                                      params.api.getServerSideSelectionState();
                                return props.allowSingleRowSelection || !selectionState?.selectAll;
                              }
                              return true;
                            }
                            : // if props.rowMultiSelectWithClick, row drag handle is shown instead of checkboxes
                            !props.rowMultiSelectWithClick,
                          // Header checkbox for "select all" - controlled by prop
                          headerCheckbox:
                              props.headerCheckboxSelection ?? false,
                          // Show disabled checkboxes (was showDisabledCheckboxes: true by default)
                          hideDisabledCheckboxes: false,
                          // Migrated from isRowSelectable prop (deprecated in v32.2)
                          ...(props.isRowSelectable && {
                            isRowSelectable: props.isRowSelectable,
                          }),
                        }
                        : undefined
                    }
                    // Configure the default selection column
                    selectionColumnDef={
                      props.hasRowSelection
                        ? {
                          width: 50,
                          maxWidth: 50,
                          minWidth: 50,
                          suppressMovable: true,
                          suppressHeaderMenuButton: true,
                          suppressHeaderContextMenu: true,
                          lockPosition: "left",
                        }
                        : undefined
                    }
                    rowDragManaged={props.rowDragging}
                    animateRows={props.rowDragging}
                    //this flag was added as recommended by ag grid as solution to freezing when ag grid component is unmounted
                    //was commented out because enabling it caused issues in cell styling accross grids
                    //downgraded version to 28 as temp fix
                    //suppressReactUi={true}
                    onGridSizeChanged={gridSizeChangeHandler}
                    // Overlay to show when there are no rows in the grid
                    noRowsOverlayComponent={NoRowsOverlay}
                    // Parameters for the NoRowsOverlay component
                    noRowsOverlayComponentParams={noRowsOverlayComponentParams}
                    //when you drag a column out of the grid (e.g. to the group zone) the column is not hidden
                    suppressDragLeaveHidesColumns
                    onBodyScrollEnd={onScrollEnded}
                    onRowDataUpdated={props.onRowDataUpdated}
                    // Tree data configuration for server-side hierarchical data
                    treeData={props.treeData}
                    {...(props.treeData && props.isServerSideGroup && {
                      isServerSideGroup: props.isServerSideGroup,
                    })}
                    {...(props.treeData && props.getServerSideGroupKey && {
                      getServerSideGroupKey: props.getServerSideGroupKey,
                    })}
                  />
                </div>
              </div>
            </div>
            {props.hideGrid && (
              <CardContainer
                card={props.card}
                cardVariant={cardVariant}
                cardColumns={props.cardColumns}
                rowData={props.rowData}
                rowCount={savedLayout?.rowCount}
                triggerLoad={(rowDataLength) => triggerCardLoad(rowDataLength)}
                loadingRows={props.loadingRows}
                //this property will add a margin top to the card container
                //if there are no filters applied to the grid or any sub bar
                //it's needed so the card container doesn't stick to the top of the card
                marginTop={
                  hasFilterChips || props.oldFiltersPanel || props.subBar
                    ? 0
                    : 2
                }
              />
            )}
          </div>
        </StyledGridWrapper>
      </>
    );
  },
);

export default SFDataGridCore;
